import { create } from 'zustand';
import { doGET, doPOST } from '../../utils/HttpUtils';
import { showError, showSuccess } from '../toaster';
import {
  INotificationItem,
  CreateGeneralNotificationRequest,
  NotificationQueryParams,
} from '../../types/notifications';

const BASE_URL = '/notifications';
const MAX_LIMIT = 100;

interface NotificationListState {
  data: INotificationItem[];
  totalPages: number;
  total: number;
  currentPage: number;
  filters: NotificationQueryParams;
  isLoading: boolean;
  limit: number;
  fetchGrid: () => Promise<void>;
  setFilters: (f: NotificationQueryParams) => void;
  nextPage: () => void;
  prevPage: () => void;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  send: (payload: CreateGeneralNotificationRequest) => Promise<{ created: number; targetUserIds: string[] } | null>;
}

const store = create<NotificationListState>((set, get) => ({
  data: [],
  totalPages: 0,
  total: 0,
  currentPage: 1,
  filters: {},
  isLoading: false,
  limit: 10,

  fetchGrid: async () => {
    try {
      const { filters, currentPage, limit, isLoading } = get();
      if (isLoading) return;

      set({ isLoading: true });

      const queryParams = new URLSearchParams();
      if (filters.userId) queryParams.append('userId', filters.userId);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.isRead !== undefined) queryParams.append('isRead', String(filters.isRead));
      if (filters.referenceType) queryParams.append('referenceType', filters.referenceType);
      if (filters.referenceId) queryParams.append('referenceId', filters.referenceId);
      queryParams.append('page', String(currentPage));
      queryParams.append('limit', String(Math.min(limit, MAX_LIMIT)));
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

      const url = `${BASE_URL}?${queryParams.toString()}`;
      const response = await doGET(url);

      if (response.status >= 200 && response.status < 400) {
        const list = response.data?.data ?? [];
        const pagination = response.data?.pagination;
        set({
          data: Array.isArray(list) ? list : [],
          totalPages: pagination?.totalPages ?? 1,
          total: pagination?.total ?? 0,
        });
      } else {
        showError(response.message || 'Failed to fetch notifications');
      }
    } catch (err) {
      showError('Failed to fetch notifications');
    } finally {
      set({ isLoading: false });
    }
  },

  setFilters: (newFilters: NotificationQueryParams) => {
    set({ filters: newFilters, currentPage: 1 });
    get().fetchGrid();
  },

  nextPage: () => {
    const { currentPage, totalPages, fetchGrid } = get();
    if (currentPage < totalPages) {
      set((s) => ({ currentPage: s.currentPage + 1 }));
      fetchGrid();
    }
  },

  prevPage: () => {
    const { currentPage, fetchGrid } = get();
    if (currentPage > 1) {
      set((s) => ({ currentPage: s.currentPage - 1 }));
      fetchGrid();
    }
  },

  onPageChange: (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    const { totalPages, fetchGrid } = get();
    const newPage = page + 1;
    if (newPage >= 1 && newPage <= totalPages) {
      set({ currentPage: newPage });
      fetchGrid();
    }
  },

  send: async (payload: CreateGeneralNotificationRequest) => {
    try {
      const response = await doPOST(`${BASE_URL}/send`, payload);
      if (response.status >= 200 && response.status < 400) {
        const result = response.data?.data;
        showSuccess(response.data?.message || 'Notification sent successfully');
        get().fetchGrid();
        return result ?? null;
      } else {
        showError(response.message || 'Failed to send notification');
        return null;
      }
    } catch (err) {
      showError('Failed to send notification');
      return null;
    }
  },
}));

export const useNotificationStore = () => store((state) => state);
